<script setup>
import { ref } from 'vue';
import NinaChat from './NinaChat.vue';
import ReportView from '../../addInsights/report.vue';
import CloserBudgetExceeded from './CloserBudgetExceeded.vue';

defineProps({
    initialMessage: { type: String, default: '' },
});

const domainInput = ref('');
const domain = ref(null);
const activeTab = ref('report');
const insightsReport = ref(null);
const insightsLoading = ref(false);
const insightsError = ref(false);
const budgetExceeded = ref(false);

const sanitizeDomain = (input) => {
    let value = input.trim();
    if (!value) return null;
    try {
        const url = value.includes('://') ? value : 'https://' + value;
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return value.replace(/^www\./, '');
    }
};

const submitDomain = async () => {
    const sanitized = sanitizeDomain(domainInput.value);
    if (!sanitized) return;

    domain.value = sanitized;
    insightsLoading.value = true;
    insightsError.value = false;
    budgetExceeded.value = false;

    try {
        const res = await axios.get(route('nina.standalone.insightsReport'), {
            params: { domain: sanitized },
        });
        budgetExceeded.value = res.data.status === 'budget_exceeded';
        insightsReport.value = res.data.report;
    } catch (e) {
        insightsError.value = true;
    } finally {
        insightsLoading.value = false;
    }
};

const reset = () => {
    domain.value = null;
    domainInput.value = '';
    insightsReport.value = null;
    insightsError.value = false;
    budgetExceeded.value = false;
    activeTab.value = 'report';
};

const handleDomainKeydown = (e) => {
    if (e.key === 'Enter') {
        e.preventDefault();
        submitDomain();
    }
};

defineExpose({ domain });
</script>

<template>
    <!-- State 1: Domain input -->
    <div v-if="!domain" class="flex flex-col items-center grow py-16">
        <div class="flex flex-col items-center grow">
            <div class="flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-aiAgentSalesAssistant">
                <i class="text-xl text-gray-800 fa-regular fa-magnifying-glass-chart scale-110"></i>
            </div>
            <h2 class="text-lg font-semibold text-gray-800 mb-2">
                <mf-translate domainkey="crm:nina.standalone.title"></mf-translate>
            </h2>
            <p class="text-sm text-gray-500 mb-6 text-center max-w-md">
                <mf-translate domainkey="crm:nina.standalone.description"></mf-translate>
            </p>
        </div>
        <div class="flex gap-2 w-full max-w-md">
            <input
                v-model="domainInput"
                type="text"
                class="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg"
                :placeholder="_mft('crm:nina.standalone.placeholder')"
                @keydown="handleDomainKeydown"
            />
            <button-base
                style-type="primary"
                @click="submitDomain"
            >
                <mf-translate domainkey="crm:nina.standalone.submit"></mf-translate>
            </button-base>
        </div>
    </div>

    <!-- State 2: Report + Chat -->
    <template v-else>
        <!-- Tab bar -->
        <div class="flex items-center justify-between border-b border-gray-200 mb-4 -mt-2">
            <div class="flex">
                <button
                    class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
                    :class="activeTab === 'report'
                        ? 'border-aiAgentSalesAssistant text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                    @click="activeTab = 'report'"
                >
                    <mf-translate domainkey="crm:nina.tab.report"></mf-translate>
                </button>
                <button
                    v-if="insightsReport && insightsReport.data"
                    class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
                    :class="activeTab === 'chat'
                        ? 'border-aiAgentSalesAssistant text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                    @click="activeTab = 'chat'"
                >
                    <mf-translate domainkey="crm:nina.tab.askNina"></mf-translate>
                </button>
            </div>
            <button-secondary
                size="sm"
                @click="reset"
            >
                <mf-translate domainkey="crm:nina.standalone.researchAnother"></mf-translate>
            </button-secondary>
        </div>

        <!-- Report tab -->
        <div v-show="activeTab === 'report'">
            <div v-if="insightsLoading" class="flex items-center justify-center py-12">
                <mf-spinner-medium class="w-24 h-12" />
            </div>

            <closer-budget-exceeded v-else-if="budgetExceeded" />

            <div v-else-if="insightsError" class="text-center py-12">
                <div class="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-gray-100">
                    <svg class="w-7 h-7 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                    </svg>
                </div>
                <p class="text-sm text-gray-500">
                    <mf-translate domainkey="crm:nina.standalone.reportError"></mf-translate>
                </p>
            </div>

            <div v-else-if="insightsReport && insightsReport.data">
                <report-view
                    :domain="domain"
                    :report="insightsReport.data"
                    :updated-at="insightsReport.updated_at"
                    section-class="pb-8 border-b"
                    section-container="div"
                />
            </div>

            <div v-else class="text-center py-12">
                <p class="text-sm text-gray-500">
                    <mf-translate domainkey="crm:nina.standalone.noReport"></mf-translate>
                </p>
            </div>
        </div>

        <!-- Chat tab -->
        <div v-show="activeTab === 'chat'">
            <nina-chat
                :lead-id="null"
                :domain="domain"
                :insights-loaded="insightsReport !== null"
                :initial-message="initialMessage"
            />
        </div>
    </template>
</template>
